import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "form",
    "submitButton",
    "addonToggle",
    "addonRow",
    "total",
    "addButtonSection",
    "cardElement",
    "cardErrors",
  ];

  connect() {
    // Get Stripe public key
    var public_key = document.querySelector("meta[name='stripe-key']").content;
    this.stripe = Stripe(public_key);

    // Set initial values - parse explicitly as floats
    this.baseTotal = parseFloat(this.data.get("baseAmount"));
    this.addonTotal = 0;

    // Check if addon is available
    this.hasAddon =
      this.data.get("addonAmount") &&
      parseFloat(this.data.get("addonAmount")) > 0;

    this.updateTotal();

    // Initialize Stripe Elements if no card on file
    const hasCardOnFile = this.data.get("hasCardOnFile");
    if (!hasCardOnFile) {
      this.setupStripeElements();
    }

    // Register the form handler once during connect
    this.registerFormSubmitHandler();
  }

  setupStripeElements() {
    // Create an instance of Elements
    this.elements = this.stripe.elements();

    // Custom styling
    const style = {
      base: {
        fontSize: "16px",
        color: "#424770",
        "::placeholder": {
          color: "#aab7c4",
        },
        padding: "10px 12px",
      },
      invalid: {
        color: "#9e2146",
      },
    };

    // Create separate elements for card number, expiry, and CVC
    this.cardNumber = this.elements.create("cardNumber", { style: style });
    this.cardExpiry = this.elements.create("cardExpiry", { style: style });
    this.cardCvc = this.elements.create("cardCvc", { style: style });

    // Mount the elements
    this.cardNumber.mount("#card-number-element");
    this.cardExpiry.mount("#card-expiry-element");
    this.cardCvc.mount("#card-cvc-element");

    // Handle real-time validation errors from the card elements
    this.cardNumber.on("change", ({ error }) => {
      this.displayError(error);
    });

    this.cardExpiry.on("change", ({ error }) => {
      this.displayError(error);
    });

    this.cardCvc.on("change", ({ error }) => {
      this.displayError(error);
    });
  }

  displayError(error) {
    const errorElement = this.cardErrorsTarget;
    if (error) {
      errorElement.textContent = error.message;
    } else {
      errorElement.textContent = "";
    }
  }

  addAddon(event) {
    event.preventDefault();

    // Only proceed if addon is available
    if (!this.hasAddon) return;

    // Add the addon product
    this.addonTotal = parseFloat(this.data.get("addonAmount"));
    this.addonRowTarget.classList.remove("hidden");

    // Hide the "Add" button section
    this.addButtonSectionTarget.classList.add("hidden");

    // Update the hidden field to true
    const includeAddonField = this.formTarget.querySelector(
      'input[name="coaching_checkout[include_addon]"]'
    );
    includeAddonField.value = true;

    this.updateTotal();
  }

  removeAddon(event) {
    event.preventDefault();

    // Only proceed if addon is available
    if (!this.hasAddon) return;

    // Remove the addon product
    this.addonTotal = 0;
    this.addonRowTarget.classList.add("hidden");

    // Show the "Add" button section again
    this.addButtonSectionTarget.classList.remove("hidden");

    // Update the hidden field to false
    const includeAddonField = this.formTarget.querySelector(
      'input[name="coaching_checkout[include_addon]"]'
    );
    includeAddonField.value = false;

    this.updateTotal();
  }

  toggleAddon(event) {
    if (event.target.checked) {
      this.addAddon(event);
    } else {
      this.removeAddon(event);
    }
  }

  updateTotal() {
    var total = this.baseTotal + this.addonTotal;
    this.totalTarget.textContent = "$" + total.toFixed(2);
  }

  // async handleStripePayment(event) {
  //   const hasCardOnFile = this.data.get("hasCardOnFile");

  //   if (hasCardOnFile) {
  //     // User has a card on file, proceed with form submission
  //     return true;
  //   }

  //   // User needs to enter card details
  //   event.preventDefault();

  //   // Disable the submit button to prevent multiple submissions
  //   const submitButton = this.submitButtonTarget;
  //   submitButton.setAttribute("disabled", "disabled");
  //   submitButton.classList.add("cursor-not-allowed", "opacity-50");
  //   submitButton.textContent = "Processing...";

  //   try {
  //     // Get the postal code
  //     const postalCode = document.getElementById("postal-code")?.value || "";

  //     // Create SOURCE instead of PaymentMethod (for older API)
  //     const { source, error } = await this.stripe.createSource(
  //       this.cardNumber,
  //       {
  //         type: "card",
  //         owner: {
  //           address: {
  //             postal_code: postalCode,
  //           },
  //         },
  //       }
  //     );

  //     if (error) {
  //       this.displayError(error);
  //       this.resetSubmitButton();
  //       return false;
  //     }

  //     // Set the source token in the hidden field
  //     const tokenField =
  //       document.getElementById("stripe_token") ||
  //       this.formTarget.querySelector(
  //         'input[name="coaching_checkout[stripe_token]"]'
  //       );

  //     if (tokenField) {
  //       tokenField.value = source.id; // Now source.id exists!
  //     } else {
  //       // Create hidden field if it doesn't exist
  //       const hiddenField = document.createElement("input");
  //       hiddenField.type = "hidden";
  //       hiddenField.name = "coaching_checkout[stripe_token]";
  //       hiddenField.value = source.id; // Now source.id exists!
  //       this.formTarget.appendChild(hiddenField);
  //     }

  //     // Submit the form
  //     this.formTarget.submit();
  //   } catch (error) {
  //     console.error("Stripe error:", error);
  //     this.displayError({
  //       message: "An unexpected error occurred. Please try again.",
  //     });
  //     this.resetSubmitButton();
  //   }
  // }

  async handleStripePayment(event) {
    const hasCardOnFile = this.data.get("hasCardOnFile");

    if (hasCardOnFile) {
      return true;
    }

    event.preventDefault();

    const submitButton = this.submitButtonTarget;
    submitButton.setAttribute("disabled", "disabled");
    submitButton.classList.add("cursor-not-allowed", "opacity-50");
    submitButton.textContent = "Processing...";

    try {
      // Use createToken instead of createSource (old Card API)
      const { token, error } = await this.stripe.createToken(this.cardNumber);

      if (error) {
        this.displayError(error);
        this.resetSubmitButton();
        return false;
      }

      // Set the token in the hidden field
      const tokenField =
        document.getElementById("stripe_token") ||
        this.formTarget.querySelector(
          'input[name="coaching_checkout[stripe_token]"]'
        );

      if (tokenField) {
        tokenField.value = token.id; // Use token.id instead of source.id
      } else {
        const hiddenField = document.createElement("input");
        hiddenField.type = "hidden";
        hiddenField.name = "coaching_checkout[stripe_token]";
        hiddenField.value = token.id; // Use token.id
        this.formTarget.appendChild(hiddenField);
      }

      this.formTarget.submit();
    } catch (error) {
      console.error("Stripe error:", error);
      this.displayError({
        message: "An unexpected error occurred. Please try again.",
      });
      this.resetSubmitButton();
    }
  }

  resetSubmitButton() {
    const submitButton = this.submitButtonTarget;
    submitButton.removeAttribute("disabled");
    submitButton.classList.remove("cursor-not-allowed", "opacity-50");
    submitButton.textContent = "Complete Purchase";
  }

  registerFormSubmitHandler() {
    this.formSubmitHandler = (event) => {
      this.handleStripePayment(event);
    };

    // Add the event listener
    this.formTarget.addEventListener("submit", this.formSubmitHandler);
  }

  disconnect() {
    // Clean up Stripe elements
    if (this.cardNumber) {
      this.cardNumber.destroy();
    }
    if (this.cardExpiry) {
      this.cardExpiry.destroy();
    }
    if (this.cardCvc) {
      this.cardCvc.destroy();
    }
  }
}
